import {
  HeaderBar,
  HeaderBarLink,
  HeaderBarLinkSection,
  HeaderBarUserSection,
  Button,
  ContextMenu,
  ContextMenuItem,
  ModalHeader,
  Modal,
  ModalFooter,
  Badge,
  Gutter,
  Suspense,
} from '@axiom/ui';
import type { User } from '@axiom/validation';
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router';

import Notifications from '../Notifications/Notifications';
import { EnvUtil } from '../../utils/env-util';
import { isTalentSuccessUser } from '../../utils/roles-util';
import { UserAccountModal } from '../UserAccountModal/UserAccountModal';
import { ManageClientsModal } from '../ManageClientsModal/ManageClientsModal';
import { AdminModal } from '../AdminModal/AdminModal';
import { ManageEmailAlertsModal } from '../ManageEmailAlertsModal/ManageEmailAlertsModal';

export type HeaderProps = {
  closeSideDrawer?: () => void;
  user?: User;
};

const closeSideDrawerDefault = () => {};

export const Header = ({
  closeSideDrawer = closeSideDrawerDefault,
  user,
}: HeaderProps) => {
  const [showUserModal, setShowUserModal] = useState(false);
  const [showManageClientModal, setShowManageClientModal] = useState(false);
  const [showEmailAlertsModal, setShowEmailAlertsModal] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const location = useLocation().pathname;

  const isEnvoyAdmin = user?.roles.includes('EnvoyAdmin');

  return (
    <HeaderBar>
      <HeaderBarLinkSection name="NavigationBar">
        {EnvUtil.enableChaxiomHeaderLink ? (
          <HeaderBarLink selected={location.startsWith('/spark')} name="Spark">
            <Link
              data-test="SPARK_LINK"
              to="/spark"
              onClick={() => {
                closeSideDrawer();
              }}
            >
              Spark
            </Link>
          </HeaderBarLink>
        ) : null}

        <HeaderBarLink
          selected={location.startsWith('/workspace')}
          name="Workspace"
        >
          <Link
            data-test="WORKSPACE_LINK"
            to="/workspace"
            onClick={() => {
              closeSideDrawer();
            }}
          >
            My Workspace
          </Link>
        </HeaderBarLink>
        {isTalentSuccessUser(user) && (
          <HeaderBarLink
            selected={location.startsWith('/bench')}
            name="MyBench"
          >
            <Link
              to="/bench"
              data-test="BENCH_LINK"
              onClick={() => {
                closeSideDrawer();
              }}
            >
              My Bench
            </Link>
          </HeaderBarLink>
        )}
        <HeaderBarLink
          selected={location.startsWith('/opportunities')}
          name="Opportunities"
        >
          <Link
            data-test="OPPORTUNITIES_LINK"
            to="/opportunities"
            onClick={() => {
              closeSideDrawer();
            }}
          >
            Opportunities
          </Link>
        </HeaderBarLink>
        <HeaderBarLink selected={location.startsWith('/talent')} name="Talent">
          <Link
            data-test="TALENT_LINK"
            to="/talent"
            onClick={() => {
              closeSideDrawer();
            }}
          >
            Talent
          </Link>
        </HeaderBarLink>
      </HeaderBarLinkSection>
      <HeaderBarUserSection>
        <Notifications />
        <ContextMenu
          direction="left"
          anchor={
            <Button
              name="UserMenuTrigger"
              icon="arrow-down"
              pattern="secondary"
              variation="minimal"
            >
              Welcome {user?.firstName}
            </Button>
          }
        >
          <ContextMenuItem
            name="MyAccountOption"
            onClick={() => {
              setShowUserModal(true);
            }}
          >
            My account
          </ContextMenuItem>
          {Boolean(isEnvoyAdmin) && (
            <ContextMenuItem
              name="AdminFunctionsOption"
              onClick={() => {
                setShowAdminModal(true);
              }}
            >
              Admin functions
            </ContextMenuItem>
          )}

          <ContextMenuItem
            name="ManageClientsOption"
            onClick={() => {
              setShowManageClientModal(true);
            }}
          >
            Manage clients
          </ContextMenuItem>
          <ContextMenuItem
            name="ManageEmailAlerts"
            onClick={() => {
              setShowEmailAlertsModal(true);
            }}
          >
            Manage email alerts
          </ContextMenuItem>
          <ContextMenuItem
            name="ReportIssueOption"
            onClick={() => {
              window.open(EnvUtil.reportAnIssueUrl);
            }}
          >
            Report an issue
          </ContextMenuItem>

          {EnvUtil.enableChaxiomHeaderLink ? null : (
            <ContextMenuItem
              name="SparkOption"
              onClick={() => {
                window.location.assign(`/spark`);
              }}
            >
              <Gutter right="8px" as="span">
                Spark
              </Gutter>
              <Badge background="green">BETA</Badge>
            </ContextMenuItem>
          )}

          <ContextMenuItem
            onClick={() => {
              window.location.assign(`${EnvUtil.loginUrl}/logout`);
            }}
            name="SIGN_OUT_LINK"
          >
            Sign out
          </ContextMenuItem>
        </ContextMenu>
      </HeaderBarUserSection>
      {showUserModal && user ? (
        <UserAccountModal
          onClose={() => {
            setShowUserModal(false);
          }}
          user={user}
          userName={`${user.firstName} ${user.lastName}`}
        />
      ) : null}
      {showManageClientModal ? (
        <ManageClientsModal
          onClose={() => {
            setShowManageClientModal(false);
          }}
        />
      ) : null}
      {showAdminModal ? (
        <Modal size="large" name="AdminFunctionsModal">
          <ModalHeader
            name="AdminFunctionsModalHeader"
            onClose={() => {
              setShowAdminModal(false);
            }}
          >
            Admin Functions
          </ModalHeader>
          <AdminModal />
          <ModalFooter>
            <Button
              name="CLOSE_BUTTON"
              onClick={() => {
                setShowAdminModal(false);
              }}
            >
              Close
            </Button>
          </ModalFooter>
        </Modal>
      ) : null}
      {showEmailAlertsModal ? (
        <Suspense fallback={null}>
          <ManageEmailAlertsModal
            onClose={() => {
              setShowEmailAlertsModal(false);
            }}
            onNavigateToCandidate={() => {
              setShowEmailAlertsModal(false);
            }}
            onNavigateToSavedSearches={() => {
              setShowEmailAlertsModal(false);
            }}
          />
        </Suspense>
      ) : null}
    </HeaderBar>
  );
};
